import { Card } from "@/components/ui/card.tsx";
import { Badge } from "@/components/ui/badge.tsx";
import { Link } from "react-router-dom";
import { Solscan } from "@/components/logos/Solscan.tsx";
import { X } from "../logos/X.tsx";
import { cn, formatLargeNumber } from "@/lib/utils.ts";
import { Persona } from "@/api/personas.ts";
import { PersonaAvatar } from "@/components/PersonaAvatar.tsx";
import { ReactNode } from "react";
import { useTokenPriceQuery } from "@/api/tokens.ts";
import { UsersIcon } from "../icons/UsersIcon.tsx";
import { Button } from "../ui/button.tsx";
import { Copy } from "lucide-react";
import { useToast } from "@/hooks/use-toast.ts";

function CardContent({
  agent,
  isPending,
  stats,
  links,
}: {
  agent: Persona;
  isPending: boolean;
  stats: any;
  links: any;
}) {
  const { toast } = useToast();
  return (
    <div>
      <div className="flex flex-row justify-between gap-2">
        <div className="flex flex-col items-start gap-y-1">
          <PersonaAvatar persona={agent} />
          <h3 className="font-semibold truncate max-w-60">{agent.name}</h3>
        </div>
        {agent.web3?.contractAddress ? (
          <div>
            <Badge
              variant="secondary"
              className="bg-[#300729] text-white text-nowrap"
            >
              Token live
            </Badge>
          </div>
        ) : null}
      </div>
      <div className="flex flex-col items-start gap-y-3 mt-2 mb-4">
        <div className="flex flex-row gap-x-2">
          {agent?.web3?.contractAddress ? (
            <div className="flex flex-row gap-x-2">
              <Button
                className="p-0 h-4 w-4 items-center justify-start text-muted-foreground hover:text-white hover:bg-transparent"
                size="sm"
                variant="ghost"
                onClick={(e) => {
                  // Prevents the user from being redirected to the persona page
                  e.preventDefault();
                  if (agent.web3?.contractAddress) {
                    navigator.clipboard
                      .writeText(agent.web3.contractAddress)
                      .then(() =>
                        toast({ description: "Address copied to clipboard." }),
                      );
                  }
                }}
              >
                <Copy className="w-4 h-4" />
              </Button>
              <span className="text-muted-foreground text-sm">
                {agent?.web3?.contractAddress?.slice(0, 7) +
                  "..." +
                  agent?.web3?.contractAddress?.slice(-10)}
              </span>
            </div>
          ) : null}
        </div>
        <div className="grid grid-cols-4 gap-y-2 gap-x-4 md:grid md:text-sm text-xs">
          <Stat
            isPending={agent.web3?.contractAddress ? isPending : false}
            label={<UsersIcon />}
            name="Holders"
            value={stats.holders}
          />
          <Stat
            isPending={agent.web3?.contractAddress ? isPending : false}
            label="V"
            name="Volume"
            value={
              stats.volume
                ? formatLargeNumber(stats.volume, { decimals: 0 })
                : undefined
            }
          />
          <Stat
            isPending={agent.web3?.contractAddress ? isPending : false}
            label="MC"
            name="Market Cap"
            value={
              stats.marketCap
                ? formatLargeNumber(stats.marketCap, { decimals: 0 })
                : undefined
            }
          />
          <Stat
            isPending={agent.web3?.contractAddress ? isPending : false}
            label="LIQ"
            name="Liquidity"
            value={
              stats.liquidity
                ? formatLargeNumber(stats.liquidity, { decimals: 0 })
                : undefined
            }
          />
        </div>
        <div className="flex flex-row gap-x-2">
          {links.solscan ? (
            <Button
              asChild
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                window.open(links.solscan, "_blank");
              }}
            >
              <Solscan height={16} width={16} />
            </Button>
          ) : null}
          {links.twitter ? (
            <Button
              variant="ghost"
              className="p-0 h-6 w-6 items-center justify-center"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                window.open(links.twitter, "_blank");
              }}
            >
              <X height={14} width={14} fill="#ffffff" />
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export function AgentCard({
  agent,
  index,
  link = true,
}: {
  agent: Persona;
  index: number;
  link?: boolean;
}) {
  const { data: metrics, isPending } = useTokenPriceQuery(
    agent.web3?.contractAddress ?? "",
    agent.web3?.pool ?? "",
    agent.web3?.blockchain ?? "",
    { enabled: Boolean(agent.web3) },
  );

  const links = {
    solscan: agent.web3?.contractAddress
      ? `https://solscan.io/token/${agent.web3.contractAddress}`
      : undefined,
    twitter: agent.twitter ? `https://x.com/${agent.twitter}` : undefined,
  };
  const stats = {
    symbol: agent.web3?.metadata?.symbol,
    holders: metrics?.marketMetrics?.holders,
    volume: metrics?.price?.volume24h,
    marketCap: metrics?.marketMetrics?.marketCap,
    liquidity: metrics?.marketMetrics?.liquidity,
  };

  return (
    <Card
      className={cn(
        link ? "cursor-pointer" : "",
        "h-56 bg-black/40 border-zinc-800 p-4 flex flex-col rounded-2xl justify-between hover:bg-black/60 transition-colors pb-0 md:min-w-[340px] w-full",
      )}
      data-agents-page-tour={index === 0 ? "first-persona-item" : undefined}
    >
      {link ? (
        <Link
          to={`/agents/${agent.persona_id}`}
          data-testid={`agent-id-${agent.persona_id}`}
        >
          <CardContent
            agent={agent}
            isPending={isPending}
            stats={stats}
            links={links}
          />
        </Link>
      ) : (
        <CardContent
          agent={agent}
          isPending={isPending}
          stats={stats}
          links={links}
        />
      )}
    </Card>
  );
}

function Stat({
  isPending,
  label,
  name,
  value,
}: {
  isPending: boolean;
  label: ReactNode;
  name: string;
  value?: string | number;
}) {
  // If value is undefined, null, or "N/A", don't render anything
  if (
    !isPending &&
    (value === undefined || value === null || value === "N/A")
  ) {
    return null;
  }

  return (
    <div className="flex flex-row gap-x-1 items-center" title={name}>
      <span className="text-gray-500">{label}</span>
      <span className={isPending ? "w-6 animate-pulse" : ""}>
        {isPending ? (
          <div className="w-6 h-3 rounded-[2px] bg-gray-500" />
        ) : (
          value
        )}
      </span>
    </div>
  );
}
